import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import { toast } from "@/hooks/use-toast";
import { getGiftLink } from "@/lib/productGiftSettings";

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  product: {
    id: string;
    name: string;
    price: number;
    image_url: string | null;
    slug: string;
    stock_quantity: number;
  };
  selected_variant?: string | null;
  variant_id?: string | null;
  is_gift?: boolean;
  parent_product_id?: string | null;
  gift_label?: string | null;
}

interface CartContextType {
  items: CartItem[];
  loading: boolean;
  itemCount: number;
  total: number;
  addToCart: (productId: string, variant?: string | null, variantId?: string) => Promise<void>;
  removeFromCart: (itemId: string) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType>({} as CartContextType);

export const useCart = () => useContext(CartContext);

const isVariantSchemaError = (error?: { code?: string; message?: string } | null) => {
  const normalized = error?.message?.toLowerCase() || "";
  return error?.code === "42703" ||
    error?.code === "PGRST204" ||
    normalized.includes("selected_variant") ||
    normalized.includes("variant_id") ||
    normalized.includes("product_variants") ||
    normalized.includes("is_gift") ||
    normalized.includes("parent_product_id") ||
    normalized.includes("gift_product_id") ||
    normalized.includes("gift_label");
};

const mapCartItems = (data: any[] = []): CartItem[] => (
  data
    .filter((item) => item?.products)
    .map((item: any) => ({
      id: item.id,
      product_id: item.product_id,
      quantity: item.quantity,
      product: item.products,
      selected_variant: item.selected_variant ?? null,
      variant_id: item.variant_id ?? null,
      is_gift: !!item.is_gift,
      parent_product_id: item.parent_product_id,
      gift_label: item.gift_label ?? null,
    }))
);

const buildVirtualGiftItems = async (items: CartItem[]): Promise<CartItem[]> => {
  const parentItems = items.filter((item) => !item.is_gift);
  if (parentItems.length === 0) return [];

  const existingGiftParents = new Set(
    items.filter((item) => item.is_gift && item.parent_product_id).map((item) => item.parent_product_id as string)
  );

  const uniqueParentIds = Array.from(new Set(parentItems.map((item) => item.product_id)));
  const giftEntries = await Promise.all(
    uniqueParentIds.map(async (productId) => ({ productId, config: await getGiftLink(productId) }))
  );

  const fallbackConfigs = giftEntries.reduce<Record<string, { gift_product_id: string; gift_label?: string | null }>>((acc, entry) => {
    if (entry.config?.gift_product_id) acc[entry.productId] = entry.config;
    return acc;
  }, {});

  const giftIds = Array.from(new Set(Object.values(fallbackConfigs).map((entry) => entry.gift_product_id)));
  if (giftIds.length === 0) return [];

  const { data: giftProducts, error } = await supabase
    .from("products")
    .select("id, name, price, image_url, slug, stock_quantity")
    .in("id", giftIds);

  if (error) {
    console.error("Failed to load fallback gift products:", error);
    return [];
  }

  const giftProductsById = new Map((giftProducts || []).map((product) => [product.id, product]));

  return parentItems.flatMap((parentItem) => {
    if (existingGiftParents.has(parentItem.product_id)) return [];

    const giftConfig = fallbackConfigs[parentItem.product_id];
    if (!giftConfig) return [];

    const giftProduct = giftProductsById.get(giftConfig.gift_product_id);
    if (!giftProduct || giftProduct.stock_quantity <= 0) return [];

    return [{
      id: `virtual-gift-${parentItem.product_id}-${giftProduct.id}`,
      product_id: giftProduct.id,
      quantity: 1,
      product: giftProduct,
      is_gift: true,
      parent_product_id: parentItem.product_id,
      gift_label: giftConfig.gift_label ?? null,
      selected_variant: null,
      variant_id: null,
    }];
  });
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [cartId, setCartId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const getOrCreateCart = useCallback(async () => {
    if (!user) return null;

    const { data: existing, error: existingError } = await supabase
      .from("carts")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle();

    if (existingError) throw existingError;
    if (existing) return existing.id;

    const { data: created, error: createError } = await supabase
      .from("carts")
      .insert({ user_id: user.id })
      .select("id")
      .single();

    if (createError) throw createError;
    return created?.id ?? null;
  }, [user]);

  const fetchCartItems = useCallback(async (id: string) => {
    const baseProductSelect = "products:product_id(id, name, price, image_url, slug, stock_quantity, brand, storage, color, condition)";
    const fullSelect = `id, product_id, quantity, variant_id, selected_variant, is_gift, parent_product_id, ${baseProductSelect}`;
    const variantOnlySelect = `id, product_id, quantity, variant_id, selected_variant, ${baseProductSelect}`;
    const basicSelect = `id, product_id, quantity, ${baseProductSelect}`;

    const { data, error } = await supabase
      .from("cart_items")
      .select(fullSelect)
      .eq("cart_id", id);

    if (error && isVariantSchemaError(error)) {
      const { data: variantFallbackData, error: variantFallbackError } = await supabase
        .from("cart_items")
        .select(variantOnlySelect)
        .eq("cart_id", id);

      if (!variantFallbackError) return mapCartItems(variantFallbackData || []);
      if (!isVariantSchemaError(variantFallbackError)) throw variantFallbackError;

      const { data: basicFallbackData, error: basicFallbackError } = await supabase
        .from("cart_items")
        .select(basicSelect)
        .eq("cart_id", id);

      if (basicFallbackError) throw basicFallbackError;
      return mapCartItems(basicFallbackData || []);
    }

    if (error) throw error;
    return mapCartItems(data || []);
  }, []);

  const refreshCart = useCallback(async () => {
    if (!user) {
      setItems([]);
      setCartId(null);
      return;
    }

    setLoading(true);
    try {
      const id = await getOrCreateCart();
      setCartId(id);
      if (!id) {
        setItems([]);
        return;
      }

      const mappedItems = await fetchCartItems(id);
      const virtualGiftItems = await buildVirtualGiftItems(mappedItems);
      setItems([...mappedItems, ...virtualGiftItems]);
    } catch (error) {
      console.error("Failed to refresh cart:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user, getOrCreateCart, fetchCartItems]);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  const addToCart = async (productId: string, variant?: string | null, variantId?: string) => {
    if (!user) {
      toast({ title: "Please sign in", description: "You need to sign in to add items to cart.", variant: "destructive" });
      return;
    }

    try {
      const id = cartId || await getOrCreateCart();
      if (!id) throw new Error("Unable to access your cart right now.");
      if (id !== cartId) setCartId(id);

      const existing = items.find((i) =>
        !i.is_gift &&
        i.product_id === productId &&
        (i.variant_id === variantId || i.selected_variant === variant || (!variantId && !i.variant_id))
      );

      if (existing) {
        const { error } = await supabase
          .from("cart_items")
          .update({ quantity: existing.quantity + 1 })
          .eq("id", existing.id);

        if (error) throw error;
      } else {
        const variantPayload = {
          cart_id: id,
          product_id: productId,
          quantity: 1,
          selected_variant: variant || null,
          variant_id: variantId || null,
        };

        let { error: insertError } = await supabase.from("cart_items").insert(variantPayload);

        if (insertError && isVariantSchemaError(insertError)) {
          const fallbackInsert = await supabase.from("cart_items").insert({
            cart_id: id,
            product_id: productId,
            quantity: 1,
          });
          insertError = fallbackInsert.error;
        }

        if (insertError?.code === "23505") {
          const { data: conflictingRow, error: conflictLookupError } = await supabase
            .from("cart_items")
            .select("id, quantity")
            .eq("cart_id", id)
            .eq("product_id", productId)
            .maybeSingle();

          if (conflictLookupError) throw conflictLookupError;
          if (!conflictingRow) throw insertError;

          let { error: conflictUpdateError } = await supabase
            .from("cart_items")
            .update({
              quantity: conflictingRow.quantity + 1,
              selected_variant: variant || null,
              variant_id: variantId || null,
            })
            .eq("id", conflictingRow.id);

          if (conflictUpdateError && isVariantSchemaError(conflictUpdateError)) {
            const fallbackUpdate = await supabase
              .from("cart_items")
              .update({ quantity: conflictingRow.quantity + 1 })
              .eq("id", conflictingRow.id);
            conflictUpdateError = fallbackUpdate.error;
          }

          if (conflictUpdateError) throw conflictUpdateError;
        } else if (insertError) {
          throw insertError;
        }
      }

      let giftAttachedName: string | null = null;
      try {
        const fallbackGiftLink = await getGiftLink(productId);
        let giftProductId = fallbackGiftLink?.gift_product_id || null;

        const { data: parentProduct, error: parentProductError } = await (supabase as any)
          .from("products")
          .select("gift_product_id, gift_label")
          .eq("id", productId)
          .maybeSingle();

        if (parentProductError && !isVariantSchemaError(parentProductError)) throw parentProductError;

        if (parentProduct?.gift_product_id) {
          giftProductId = parentProduct.gift_product_id;
        }

        if (giftProductId) {
          const { data: giftProduct, error: giftLookupError } = await (supabase as any)
            .from("products")
            .select("id, name, stock_quantity")
            .eq("id", giftProductId)
            .maybeSingle();

          if (giftLookupError) throw giftLookupError;

          if (giftProduct && giftProduct.stock_quantity > 0) {
            const giftAlready = items.find(
              (i) => i.is_gift && i.parent_product_id === productId && i.product_id === giftProductId,
            );

            if (!giftAlready) {
              let attachedVirtually = false;
              let { error: giftInsertError } = await supabase.from("cart_items").insert({
                cart_id: id,
                product_id: giftProductId,
                quantity: 1,
                is_gift: true,
                parent_product_id: productId,
              });

              if (giftInsertError && isVariantSchemaError(giftInsertError)) {
                attachedVirtually = true;
                giftInsertError = null;
              }

              if (giftInsertError && giftInsertError.code !== "23505") {
                throw giftInsertError;
              }

              if (!giftInsertError || attachedVirtually) {
                giftAttachedName = giftProduct.name;
              }
            }
          }
        }
      } catch (err) {
        console.error("Gift auto-attach failed:", err);
      }

      await refreshCart();
      toast({
        title: "Added to cart!",
        description: giftAttachedName ? `🎁 Free ${giftAttachedName} added to your order` : undefined,
      });
    } catch (error: any) {
      console.error("Add to cart failed:", error);
      toast({
        title: "Could not add to cart",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  const removeFromCart = async (itemId: string) => {
    const target = items.find((i) => i.id === itemId);
    if (target?.is_gift) {
      toast({ title: "This is a free gift", description: "Remove the parent product to remove the gift.", variant: "destructive" });
      return;
    }
    await supabase.from("cart_items").delete().eq("id", itemId);
    if (target && cartId) {
      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("cart_id", cartId)
        .eq("is_gift", true)
        .eq("parent_product_id", target.product_id);

      if (error && !isVariantSchemaError(error)) {
        throw error;
      }
    }
    await refreshCart();
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    const target = items.find((i) => i.id === itemId);
    if (target?.is_gift) return;
    if (quantity <= 0) return removeFromCart(itemId);
    await supabase.from("cart_items").update({ quantity }).eq("id", itemId);
    await refreshCart();
  };

  const clearCart = async () => {
    if (!cartId) return;
    await supabase.from("cart_items").delete().eq("cart_id", cartId);
    await refreshCart();
  };

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const total = items.reduce((sum, i) => sum + (i.is_gift ? 0 : i.product.price * i.quantity), 0);

  return (
    <CartContext.Provider value={{ items, loading, itemCount, total, addToCart, removeFromCart, updateQuantity, clearCart, refreshCart }}>
      {children}
    </CartContext.Provider>
  );
};
